import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { loadAuthState } from '../lib/auth-store';
import {
  useAuthSession,
  useCompleteSignupCheckout,
  useForgotPassword,
  useStartSignupCheckout,
} from '../lib/hooks';
import { Button, Field, Icon, InlineAlert } from '../components/ui';

type AuthMode = 'login' | 'signup';

function slugifyTenant(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = loadAuthState();
  const { loginMutation } = useAuthSession();
  const forgotPasswordMutation = useForgotPassword();
  const signupCheckoutMutation = useStartSignupCheckout();
  const completeSignupMutation = useCompleteSignupCheckout();

  const [mode, setMode] = useState<AuthMode>('login');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [processedSessionId, setProcessedSessionId] = useState<string | null>(null);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');

  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupTenantName, setSignupTenantName] = useState('');
  const [signupPassword, setSignupPassword] = useState('');

  const signupTenantSlug = useMemo(() => slugifyTenant(signupTenantName), [signupTenantName]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');

    if (checkout === 'cancel') {
      setMessage('Checkout was cancelled. You can pick up where you left off whenever you are ready.');
      return;
    }

    if (
      checkout !== 'success' ||
      !sessionId ||
      auth.accessToken ||
      completeSignupMutation.isPending ||
      processedSessionId === sessionId
    ) {
      return;
    }

    setProcessedSessionId(sessionId);

    void completeSignupMutation
      .mutateAsync(sessionId)
      .then(() => {
        navigate('/dashboard', { replace: true });
      })
      .catch((checkoutError) => {
        setError((checkoutError as Error).message);
      });
  }, [auth.accessToken, completeSignupMutation, location.search, navigate, processedSessionId]);

  if (auth.accessToken) {
    return <Navigate to="/dashboard" replace />;
  }

  async function onLogin(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    try {
      await loginMutation.mutateAsync({ email: loginEmail, password: loginPassword });
      const redirect = (location.state as { from?: string } | null)?.from || '/dashboard';
      navigate(redirect, { replace: true });
    } catch (loginError) {
      setError((loginError as Error).message);
    }
  }

  async function onSignup(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    try {
      const currentBase = window.location.origin;
      const checkout = await signupCheckoutMutation.mutateAsync({
        tenantName: signupTenantName,
        tenantSlug: signupTenantSlug,
        name: signupName,
        email: signupEmail,
        password: signupPassword,
        successUrl: `${currentBase}/login?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${currentBase}/login?checkout=cancel`,
      });

      if (!checkout.url) {
        setError('We could not start the checkout. Please try again in a moment.');
        return;
      }

      window.location.href = checkout.url;
    } catch (signupError) {
      setError((signupError as Error).message);
    }
  }

  async function sendForgotPassword() {
    setError(null);
    setMessage(null);

    try {
      const response = await forgotPasswordMutation.mutateAsync(forgotEmail || loginEmail);
      setMessage(response.message);
    } catch (forgotError) {
      setError((forgotError as Error).message);
    }
  }

  const busy =
    loginMutation.isPending ||
    forgotPasswordMutation.isPending ||
    signupCheckoutMutation.isPending ||
    completeSignupMutation.isPending;

  return (
    <div className="auth-shell">
      <div className="auth-panel">
        <div className="auth-left">
          <h1>Keep every Google Business Profile active. Automatically.</h1>
          <p>
            Connect your clients&rsquo; locations once, set a posting schedule, and fresh local
            content goes out on time — every week, without anyone lifting a finger.
          </p>
          <ul className="auth-points">
            <li>
              <Icon.Check size={16} />
              Posts are written for each business, reviewed against Google&rsquo;s content
              policies, and published on schedule.
            </li>
            <li>
              <Icon.Check size={16} />
              Review replies handled for you, in your client&rsquo;s tone of voice.
            </li>
            <li>
              <Icon.Check size={16} />
              A full activity log shows exactly what was published, when, and where.
            </li>
            <li>
              <Icon.Check size={16} />
              White-label ready: your logo, your colors, your domain.
            </li>
          </ul>
          <span className="auth-footnote">
            Built for marketing agencies managing local business profiles at scale.
          </span>
        </div>

        <div className="auth-right">
          <div className="auth-tabs segmented">
            <button type="button" className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')}>
              Sign in
            </button>
            <button type="button" className={mode === 'signup' ? 'active' : ''} onClick={() => setMode('signup')}>
              Create account
            </button>
          </div>

          {mode === 'login' && (
            <form className="auth-card" onSubmit={onLogin}>
              <h2>Welcome back</h2>
              <p className="auth-subtitle">Sign in to manage your locations and posting schedules.</p>
              <Field label="Email">
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="you@youragency.com"
                  value={loginEmail}
                  onChange={(event) => setLoginEmail(event.target.value)}
                  required
                />
              </Field>
              <Field label="Password">
                <input
                  type="password"
                  autoComplete="current-password"
                  placeholder="Your password"
                  value={loginPassword}
                  onChange={(event) => setLoginPassword(event.target.value)}
                  required
                />
              </Field>

              {error && <InlineAlert variant="danger">{error}</InlineAlert>}
              {message && <InlineAlert variant="success">{message}</InlineAlert>}

              <Button type="submit" loading={loginMutation.isPending} loadingText="Signing in…" disabled={busy}>
                Sign in
              </Button>

              <button
                className="link-like"
                type="button"
                onClick={() => {
                  setShowForgotPassword((current) => !current);
                  setForgotEmail(loginEmail);
                }}
                style={{ justifySelf: 'flex-start' }}
              >
                Forgot your password?
              </button>

              {showForgotPassword && (
                <div className="auth-inline-reset">
                  <Field
                    label="Account email"
                    hint="We will email you a secure link to choose a new password."
                  >
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(event) => setForgotEmail(event.target.value)}
                      required
                    />
                  </Field>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={sendForgotPassword}
                    loading={forgotPasswordMutation.isPending}
                    loadingText="Sending…"
                    disabled={busy}
                  >
                    Email me a reset link
                  </Button>
                </div>
              )}
            </form>
          )}

          {mode === 'signup' && (
            <form className="auth-card" onSubmit={onSignup}>
              <h2>Create your agency workspace</h2>
              <p className="auth-subtitle">
                Start your subscription, then connect your first Google Business Profile in minutes.
              </p>
              <Field label="Your name">
                <input
                  value={signupName}
                  placeholder="Alex Rivera"
                  onChange={(event) => setSignupName(event.target.value)}
                  required
                />
              </Field>
              <Field label="Work email">
                <input
                  type="email"
                  placeholder="you@youragency.com"
                  value={signupEmail}
                  onChange={(event) => setSignupEmail(event.target.value)}
                  required
                />
              </Field>
              <Field
                label="Agency name"
                hint={
                  signupTenantSlug
                    ? `Your workspace address will be “${signupTenantSlug}”.`
                    : 'This is the name your team and clients will see.'
                }
              >
                <input
                  value={signupTenantName}
                  onChange={(event) => setSignupTenantName(event.target.value)}
                  placeholder="Bright Local Marketing"
                  required
                />
              </Field>
              <Field label="Password" hint="Use at least 12 characters. A short sentence works great.">
                <input
                  type="password"
                  autoComplete="new-password"
                  value={signupPassword}
                  onChange={(event) => setSignupPassword(event.target.value)}
                  placeholder="Minimum 12 characters"
                  required
                />
              </Field>
              {error && <InlineAlert variant="danger">{error}</InlineAlert>}
              {message && <InlineAlert variant="success">{message}</InlineAlert>}
              <Button
                type="submit"
                loading={signupCheckoutMutation.isPending}
                loadingText="Opening secure checkout…"
                disabled={busy}
              >
                Continue to secure checkout
              </Button>
              <p className="muted" style={{ margin: 0 }}>
                Payment is processed securely by Stripe. You can cancel anytime.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
